import promptGalleryData from '../data/promptGalleryData.json';
import { getExternalConsumptionAPI } from '../../../../utils/httpUtilsExternalAPI';
import axios from 'axios';

export interface PromptGalleryItem {
  questionText: string;
  task: string;
  knowledgeArea: string;
  rank: number;
  createdOn: string;
  title: string;
}

export class PromptGalleryService {
  private static instance: PromptGalleryService;
  private cachedData: PromptGalleryItem[] = [];
  private isDataLoaded: boolean = false;
  private readonly SESSION_STORAGE_KEY = 'mcaps_prompt_gallery_data';
  private readonly SESSION_STORAGE_TIMESTAMP_KEY = 'mcaps_prompt_gallery_timestamp';
  private readonly SESSION_STORAGE_APPNAME_KEY = 'mcaps_prompt_gallery_appName';
  private readonly CACHE_DURATION_MS = 24 * 60 * 60 * 1000;
  
  private constructor() {
    this.loadFromSessionStorage();
  }

  public static getInstance(): PromptGalleryService {
    if (!PromptGalleryService.instance) {
      PromptGalleryService.instance = new PromptGalleryService();
    }
    return PromptGalleryService.instance;
  }

  private loadFromSessionStorage(): void {
    try {
      const cachedData = sessionStorage.getItem(this.SESSION_STORAGE_KEY);
      const cachedTimestamp = sessionStorage.getItem(this.SESSION_STORAGE_TIMESTAMP_KEY);
      const cachedAppName = sessionStorage.getItem(this.SESSION_STORAGE_APPNAME_KEY) || '';
      const currentAppName = sessionStorage.getItem('mcapsCopilot_appName') || '';
      
      if (cachedData && cachedTimestamp) {
        const timestamp = parseInt(cachedTimestamp, 10);
        const now = Date.now();
        
        // Invalidate cache if appName changed or cache expired
        if (now - timestamp < this.CACHE_DURATION_MS && cachedAppName === currentAppName) {
          this.cachedData = JSON.parse(cachedData);
          this.isDataLoaded = true;
        } else {
          this.clearSessionStorage();
        }
      } else {
      }
    } catch (error) {
      console.error('❌ Error loading from session storage:', error);
      this.clearSessionStorage();
    }
  }

  private saveToSessionStorage(data: PromptGalleryItem[]): void {
    try {
      sessionStorage.setItem(this.SESSION_STORAGE_KEY, JSON.stringify(data));
      sessionStorage.setItem(this.SESSION_STORAGE_TIMESTAMP_KEY, Date.now().toString());
      sessionStorage.setItem(this.SESSION_STORAGE_APPNAME_KEY, sessionStorage.getItem('mcapsCopilot_appName') || '');
    } catch (error) {
      console.error('❌ Failed to save to session storage:', error);
    }
  }

  private clearSessionStorage(): void {
    try {
      sessionStorage.removeItem(this.SESSION_STORAGE_KEY);
      sessionStorage.removeItem(this.SESSION_STORAGE_TIMESTAMP_KEY);
      sessionStorage.removeItem(this.SESSION_STORAGE_APPNAME_KEY);
      this.cachedData = [];
      this.isDataLoaded = false;
    } catch (error) {
      // Silent fail
    }
  }

  public async loadPromptGalleryDataFromApi(parentContext: any, getAccessTokenForAPICall: any): Promise<PromptGalleryItem[]> {
    try {
      // Require authClient and token to make the API call; don't depend on telemetry client
      if (parentContext?.authClient && getAccessTokenForAPICall?.token) {
        const urlPath = 'api/SharePointRAGStaticQuestions/GetQuestionsByFilters';
        
        // Append tile-specific appName if available for filtered prompts
        const appName = sessionStorage.getItem('mcapsCopilot_appName');
        const apiUrl = appName ? `${urlPath}?appName=${encodeURIComponent(appName)}` : urlPath;
        
        const response = await getExternalConsumptionAPI(apiUrl, parentContext.authClient);
        
        if (response.status === 200) {
          let apiData: PromptGalleryItem[];
          
          if (Array.isArray(response.data)) {
            apiData = response.data;
          } else if (response.data && Array.isArray(response.data.data)) {
            apiData = response.data.data;
          } else if (response.data && Array.isArray(response.data.result)) {
            apiData = response.data.result;
          } else {
            throw new Error('API response is not in expected array format');
          }
          
          this.cachedData = apiData;
          this.isDataLoaded = true;
          this.saveToSessionStorage(apiData);
          
          return apiData;
        } else if (response?.status === 404 || response?.status === 204) {
          throw new Error('No data found from API');
        }
      }
      
  throw new Error('Context or auth token not available');
    } catch (error) {
      console.error('❌ API call failed, using JSON fallback:', error);
      // Fallback: populate from JSON and persist to session so UI can use it
      const fallback = this.getJsonFallbackData();
      this.cachedData = fallback;
      this.isDataLoaded = Array.isArray(fallback) && fallback.length > 0;
      this.saveToSessionStorage(fallback);
      return fallback;
    }
  }

  public getJsonFallbackData(): PromptGalleryItem[] {
    return (promptGalleryData as any).promptGalleryData || [];
  }

  public getUniqueTaskTypes(data: PromptGalleryItem[]): string[] {
    const tasks = [...new Set(data.map(item => item.task))];
    return tasks.sort();
  }

  public getUniqueKnowledgeAreas(data: PromptGalleryItem[]): string[] {
    const areas = [...new Set(data.map(item => item.knowledgeArea))];
    return areas.sort();
  }

  public getCachedData(): PromptGalleryItem[] {
    // Always reload from session storage to get latest data
    this.loadFromSessionStorage();
    
    if (!this.isDataLoaded || this.cachedData.length === 0) {
      return [];
    }
    
    const sorted = [...this.cachedData].sort((a, b) => (a.rank ?? 999) - (b.rank ?? 999));
    const rankedAboveSix = sorted.filter(item => (item.rank ?? 0) > 6);
    if (rankedAboveSix.length >= 10) {
      return rankedAboveSix.slice(0, 10);
    }
    return rankedAboveSix.length > 0 ? rankedAboveSix : sorted.slice(0, 10);
  }

  public getSuggestionsData(): PromptGalleryItem[] {
    // Always reload from session storage to get latest data
    this.loadFromSessionStorage();
    
    if (this.isDataLoaded && this.cachedData.length > 0) {
      return this.cachedData.filter(item => (item.rank || 999) <= 6).slice(0, 6);
    }
    
    // Session storage empty - API hasn't completed yet or failed
    return [];
  }

  public hasApiData(): boolean {
    return this.isDataLoaded && this.cachedData.length > 0;
  }

  public invalidateCache(): void {
    this.clearSessionStorage();
  }

  public async forceRefreshData(parentContext: any, getAccessTokenForAPICall: any): Promise<void> {
    if (parentContext && getAccessTokenForAPICall) {
      try {
        await this.loadPromptGalleryDataFromApi(parentContext, getAccessTokenForAPICall);
      } catch (error) {
        // Silent error handling
      }
    }
  }

  public filterPromptGalleryData(
    data: PromptGalleryItem[],
    selectedTasks: string[],
    selectedKnowledgeAreas: string[]
  ): PromptGalleryItem[] {
    return data.filter(item => {
      const taskMatch = selectedTasks.length === 0 || selectedTasks.includes(item.task);
      const areaMatch = selectedKnowledgeAreas.length === 0 || selectedKnowledgeAreas.includes(item.knowledgeArea);
      return taskMatch && areaMatch;
    });
  }

  public static async initializeGlobalPromptData(parentContext: any, getAccessTokenForAPICall: any): Promise<void> {
    const instance = PromptGalleryService.getInstance();
    
    if (parentContext && getAccessTokenForAPICall) {
      try {
        // Check if appName changed since last cache or cache expired — if so, force re-fetch
        const currentAppName = sessionStorage.getItem('mcapsCopilot_appName') || '';
        const cachedAppName = sessionStorage.getItem(instance.SESSION_STORAGE_APPNAME_KEY) || '';
        const cachedTimestamp = sessionStorage.getItem(instance.SESSION_STORAGE_TIMESTAMP_KEY);
        const now = Date.now();
        const isCacheValid = cachedTimestamp && (now - parseInt(cachedTimestamp, 10) < instance.CACHE_DURATION_MS);

        if (instance.isDataLoaded && cachedAppName === currentAppName && isCacheValid) {
          return; // Cache is valid for current tile context
        }
        await instance.loadPromptGalleryDataFromApi(parentContext, getAccessTokenForAPICall);
      } catch (error) {
        // Silent error handling, will use JSON fallback when needed
      }
    } else {
    }
  }
}

export const promptGalleryService = PromptGalleryService.getInstance();

// Generated by Copilot
// updated on: 2026-03-19 | 4 lines added | [Auto Generated] | Zero-deploy tile routing: added public invalidateCache() method to allow external callers to clear prompt gallery cache when copilot appName changes between tiles | Time Taken 5 min